import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { GetStaticPaths, GetStaticProps, GetStaticPropsContext } from 'next';
import Head from 'next/head';
import { sanitize } from 'isomorphic-dompurify';
import parse from 'html-react-parser';
import { useEffect } from 'react';

import { usePostContext } from 'providers/PostProvider';
import { Post as PostType, Page as PageType, Translation } from 'gql/graphql';
import client from 'client/apollo-client';
import {
  SinglePostQuery,
  SinglePageQuery,
  TotalPostsQuery,
  AllPostsQuery,
  TotalPagesQuery,
  AllPagesQuery,
  DefaultStickyCtaQuery,
} from 'queries';
import getHrefs from 'helpers/getHrefs';
import Body from 'components/Layout/Body';
import Footer from 'components/Footer/Footer';
import TopNav from 'components/Navigation/TopNav';
import Post from 'components/Post/Post';
import PageContent from 'components/Page/PageContent';
import returnNotFound from 'libs/returnNotFound';
import { useTrackEventViewedPage } from 'hooks/useTrackEventViewedPage';
import { useTrackEventViewedBlogPage } from 'hooks/useTrackEventViewedBlogPage';

import clearHead from '../helpers/clearHead';
import getFullSchemaOrg, { WpAuthorship } from '../helpers/getFullSchemaOrg';

export const STICKY_CTA_QUERIES = {
  en: 'defaultEnglishStickyCta',
  es: 'defaultSpanishStickyCta',
  de: 'defaultGermanStickyCta',
  fr: 'defaultFrenchStickyCta',
  pt: 'defaultPortugueseStickyCta',
};

interface PageWithAuthorshipType extends PageType {
  authorship: WpAuthorship[];
}

interface PostWithAuthorshipType extends PostType {
  authorship: WpAuthorship[];
}

interface PageComponentProps {
  type: Blog.PageType;
  post: PostWithAuthorshipType | PageWithAuthorshipType;
  locale: Blog.LocaleType;
  translations: Blog.TranslationType[];
}

export default function Page(props: PageComponentProps) {
  const { post, locale, translations, type } = props;
  const { dispatch } = usePostContext();

  useEffect(() => {
    dispatch({
      type: 'SET_POST',
      payload: {
        contentAssetType: post.contentAssetType,
        trackAsGatedContent:
          post.__typename === 'Page'
            ? post?.gatedContentTracking?.trackAsGatedContent
            : null,
      },
    });
  }, [post]);

  useTrackEventViewedPage({
    page_name: 'post',
    //@ts-expect-error gatedContentTracking does exist on Page
    ...(type === 'page' && post.gatedContentTracking?.trackAsGatedContent
      ? { content_type: 'gated_content' }
      : {}),
    post_type: post.contentAssetType,
  });
  useTrackEventViewedBlogPage();

  //get full SEO data for post by slug
  //parse to jsx
  const clearedHead = clearHead(post?.seo.fullHead);
  const headSEO = post?.seo.fullHead ? parse(clearedHead) : null;
  const schemaObj = getFullSchemaOrg(post?.seo.schema.raw, post?.authorship);
  const schema = schemaObj ? JSON.stringify(schemaObj) : null;

  //also, now that we have translations
  //we can build our hreflang tags for the Head
  const hrefs = getHrefs(translations);

  if (type === 'post') {
    return (
      <div data-testid="post" className={type}>
        <Head>
          {headSEO}
          {hrefs.map((cur, index) => {
            return { ...cur, key: index };
          })}
          <script
            key="yoast-schema"
            type="application/ld+json"
            className="yoast-schema-graph"
            dangerouslySetInnerHTML={{ __html: sanitize(schema) }}
          />
        </Head>
        <TopNav locale={locale} preview={false} translations={translations} />
        <Body>
          <Post asPreview={false} locale={locale} post={post as PostType} />
        </Body>
        <Footer translations={translations} locale={locale} />
      </div>
    );
  } else if (props.type === 'page') {
    return (
      <div data-testid="page" className={type}>
        <Head>
          {headSEO}
          {hrefs.map((cur, index) => {
            return { ...cur, key: index };
          })}
          <script
            key="yoast-schema"
            type="application/ld+json"
            className="yoast-schema-graph"
            dangerouslySetInnerHTML={{ __html: sanitize(schema) }}
          />
        </Head>
        <TopNav locale={locale} preview={false} translations={translations} />
        <Body>
          <PageContent page={post as PageType} />
        </Body>
        <Footer translations={translations} locale={locale} />
      </div>
    );
  }
}

export const getStaticProps: GetStaticProps = async (
  context: GetStaticPropsContext
) => {
  const locale =
    (context?.locale as Blog.LocaleType) ?? ('en' as Blog.LocaleType);
  const { postSlug } = context.params;

  const uri = '/' + postSlug;

  const { data: postData } = await client.query({
    query: SinglePostQuery,
    variables: {
      slug: postSlug,
    },
  });

  if (!postData.posts?.nodes[0] || postData.posts.nodes[0] === undefined) {
    const { data: pageData } = await client.query({
      query: SinglePageQuery,
      variables: {
        uri: uri,
      },
    });

    if (!pageData.pageBy) {
      return returnNotFound(locale, `/${locale}${uri}`);
    }

    const transData = pageData.pageBy?.translations;

    const translations =
      transData && mapTranslations(transData, locale, postSlug as string);

    return {
      props: {
        ...(await serverSideTranslations('en', ['common'])),
        post: pageData.pageBy,
        locale: 'en',
        translations: translations,
        type: 'page' as Blog.PageType,
      },
    };
  }

  //here we check if the current post has any available translations
  //WPML stores all translations in a data table that share the same post id
  //then we use wpgraphql-wpml to include translations in our post graphql data
  const transData: Translation[] = postData.posts.nodes[0].translations;

  const translations = mapTranslations(transData, locale, postSlug as string);

  const postPropData = { ...postData.posts.nodes[0] };

  postPropData.stickyCta = await getPostStickyCTA(postPropData, locale);

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      post: postPropData as PostType,
      locale: locale,
      translations: translations,
      type: 'post' as Blog.PageType,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.NEXT_PUBLIC_EMERGENCY_DEPLOY === 'true') {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const totalPostsQuery = await client.query({
    query: TotalPostsQuery,
    variables: {},
  });

  const total = totalPostsQuery.data?.posts?.pageInfo?.total ?? null;

  const postPages = Math.ceil(parseInt(total) / 100);

  const paths = [];

  let endCursor = null;

  for (let i = 0; i < postPages; i++) {
    const { data } = await client.query({
      query: AllPostsQuery,
      variables: {
        after: endCursor,
      },
    });

    const pagePaths = data.posts.nodes.map((cur) => ({
      params: {
        postSlug: cur.slug,
      },
      locale: cur.locale?.locale.slice(0, 2) ?? 'en',
    }));

    paths.push(...pagePaths);

    endCursor = data.posts.pageInfo.endCursor;
  }

  const totalPagesQuery = await client.query({
    query: TotalPagesQuery,
    variables: {},
  });

  const totalPages = totalPagesQuery.data?.pages?.pageInfo?.total ?? null;

  const pagePages = Math.ceil(parseInt(totalPages) / 100);

  endCursor = null;

  for (let i = 0; i < pagePages; i++) {
    const { data } = await client.query({
      query: AllPagesQuery,
      variables: {
        after: endCursor,
      },
    });

    const pageNodes = data.pages.nodes;
    const filterNodes = pageNodes.filter((cur) => cur.slug !== '/root/');

    const pagePaths = filterNodes.map((cur) => {
      const { slug } = cur;
      return {
        params: {
          postSlug: slug,
        },
        locale: cur.locale?.locale.slice(0, 2) ?? 'en',
      };
    });

    paths.push(...pagePaths);
  }

  return {
    paths: paths,
    fallback: 'blocking',
  };
};

export function mapTranslations(
  arr: Translation[],
  locale: Blog.LocaleType,
  postSlug: string
): Blog.TranslationType[] {
  const transArray = arr.map((cur) => {
    //a locale value would actually look like "es_ES"
    //so we need to split the first two characters off
    const code = cur?.locale?.slice(0, 2) as Blog.LocaleType;
    const transObj = {
      code: code,
    };
    // now we grab the slug from the provided uri
    if (cur.href) {
      const pathSplit = cur.href.split('/');
      //remove empty entries
      let pathFilter = pathSplit.filter((a) => a);
      pathFilter = pathFilter.filter((a) => a[0] !== '?');
      //get slug
      transObj['pathname'] = pathFilter[pathFilter.length - 1];
    }

    return transObj as Blog.TranslationType;
  });

  //we also have to include the current post
  const curObj: Blog.TranslationType = {
    code: locale,
    pathname: postSlug,
    current: true,
  };

  transArray.push(curObj);

  return transArray;
}

const getPostStickyCTA = async (post: PostType, locale: Blog.LocaleType) => {
  if (post.stickyCta?.stickyCtaSelect) {
    return post.stickyCta.stickyCtaSelect?.stickyCtaFields?.stickyCtaFieldGroup;
  } else {
    const vars = {};
    vars[locale] = true;

    const { data: stickyCtaData } = await client.query({
      query: DefaultStickyCtaQuery,
      variables: vars,
    });

    return (
      stickyCtaData.acfOptions?.acfOptionsFields?.defaultStickyCtas?.[
        STICKY_CTA_QUERIES[locale]
      ]?.stickyCtaFields?.stickyCtaFieldGroup ?? null
    );
  }
};
